import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Box, Briefcase, Package, Mail, Shield, Factory } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== "undefined") ? process.env.REACT_APP_BACKEND_URL : "";
const API = `${BACKEND_URL}/api`;

const navItems = [
  { id: 'home', label: 'Home', icon: Home, path: '/#home', section: 'home' },
  { id: 'about', label: 'About', icon: Briefcase, path: '/#about', section: 'about' },
  { id: 'products', label: 'Products', icon: Box, path: '/products' },
  { id: 'services', label: 'Services', icon: Package, path: '/services' },
  { id: 'supplies', label: 'Supplies', icon: Package, path: '/#supplies', section: 'supplies' },
  { id: 'contact', label: 'Contact Us', icon: Mail, path: '/#contact', section: 'contact' }
];

export const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    loadSiteSettings();
  }, []);

  const loadSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      setSiteSettings(response.data);
    } catch (error) {
      console.error('Error loading site settings:', error);
    }
  };

  const handleNavClick = (item) => {
    if (item.section && (location.pathname === '/' || location.pathname === '/#')) {
      // If already on Home, scroll immediately
      const element = document.getElementById(item.section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    // Otherwise, the NavLink 'to' prop will handle the URL change to /#section,
    // and the HomePage useEffect will catch it on load.
    
    if (onClose) onClose();
  };

  const isActive = (item) => {
    if (item.path !== '/') {
      return location.pathname === item.path;
    }
    return false;
  };

  return (
    <motion.aside
      initial={{ x: -280 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.3 }}
      className="h-full bg-[#121212] border-r border-[#27272A] flex flex-col"
    >
      <div className="p-6 border-b border-[#27272A]">
        <NavLink 
          to="/#home" 
          onClick={() => handleNavClick({ section: 'home' })}
          className="flex items-center gap-3 mb-2 hover:opacity-80 transition-opacity cursor-pointer block"
        >
          {siteSettings?.logo_url ? (
            <img src={siteSettings.logo_url} alt="Logo" className="w-10 h-10 object-contain" />
          ) : (
            <div className="w-10 h-10 bg-[#FF9900] flex items-center justify-center font-bold text-black text-xl">
              E
            </div>
          )}
          <div>
            <h1 className="text-lg font-bold text-[#FF9900] leading-tight">
              {siteSettings?.company_name?.split(' ').slice(0, 2).join(' ').toUpperCase() || 'EASTEND IRON'}
            </h1>
            <p className="text-xs text-gray-400">
              {siteSettings?.company_name?.split(' ').slice(2).join(' ').toUpperCase() || 'INDUSTRIES LTD.'}
            </p>
          </div>
        </NavLink>
        <p className="text-xs text-gray-500 mt-2">
          {siteSettings?.tagline ? siteSettings.tagline.replace(/&amp;/g, '&') : 'Specializing in Shop & Portable'}
        </p>
      </div>

      <nav className="flex-1 py-6">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          
          return (
            <NavLink
              key={item.id}
              to={item.path}
              onClick={() => handleNavClick(item)}
              data-testid={`nav-${item.id}`}
              className={`flex items-center gap-3 px-6 py-3 transition-all duration-300 border-l-4 ${
                active
                  ? 'bg-[#1E1E1E] border-[#FF9900] text-[#FF9900]'
                  : 'border-transparent text-gray-400 hover:bg-[#1E1E1E] hover:text-[#FF9900] hover:border-[#FF9900]/50'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium uppercase tracking-wide text-sm">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="p-6 border-t border-[#27272A]">
        <p className="text-xs text-gray-600">© {new Date().getFullYear()} Eastend Iron Industries Ltd.</p>
      </div>
    </motion.aside>
  );
};

export default Sidebar;